/**
 * statusHistory reducer (VE-26730). Invariants: history records stay unique by
 * id and createdDateTime-descending (mergeHistoryPage); a 'disabled' gate drops
 * every fetch mutation so the slice stays inert when the feature is
 * unprovisioned (US-9).
 */
import { createReducer } from '@reduxjs/toolkit';
import {
  FETCH_LATEST_STATUS_ROW,
  FETCH_LATEST_STATUS_ROW_FAILURE,
  FETCH_LATEST_STATUS_ROW_SUCCESS,
  FETCH_STATUS_HISTORY,
  FETCH_STATUS_HISTORY_FAILURE,
  FETCH_STATUS_HISTORY_SUCCESS,
  MARK_TDO_READ_VERIFIED,
  RESET_STATUS_HISTORY,
  STATUS_HISTORY_GATE_RESOLVED,
} from '../actions';
import { mergeHistoryPage } from '../models';
import { defaultState, namespace } from '../store';

const newEmptyPage = () => ({
  records: [],
  count: 0,
  isLoading: false,
  error: null,
});

const reducer = createReducer(defaultState, (builder) => {
  builder
    .addCase(STATUS_HISTORY_GATE_RESOLVED, (state, action) => {
      state.gate = action.payload.enabled ? 'enabled' : 'disabled';
      state.schemaId = action.payload.schemaId ?? null;
    })
    .addCase(MARK_TDO_READ_VERIFIED, (state, action) => {
      // Deliberately NOT gated on 'disabled': verification is an authorization
      // fact about the caller and TDO, orthogonal to whether the feature is
      // provisioned — a gate that settles later must not have to re-verify.
      state.readVerifiedTdoIds[action.payload.tdoId] = true;
    })
    .addCase(FETCH_LATEST_STATUS_ROW, () => {
      // Latest-row reads have no spinner; state changes on success/failure only.
    })
    .addCase(FETCH_LATEST_STATUS_ROW_SUCCESS, (state, action) => {
      if (state.gate === 'disabled') {
        return;
      }
      state.latestByTdoId[action.payload.tdoId] = action.payload.record;
    })
    .addCase(FETCH_LATEST_STATUS_ROW_FAILURE, () => {
      // Best-effort read: keep whatever we had (possibly nothing) — the icon
      // and pop-up simply don't show. Failures are logged at the service.
    })
    .addCase(FETCH_STATUS_HISTORY, (state, action) => {
      if (state.gate === 'disabled') {
        return;
      }
      const page = state.historyByTdoId[action.payload.tdoId] ?? newEmptyPage();
      page.isLoading = true;
      page.error = null;
      state.historyByTdoId[action.payload.tdoId] = page;
    })
    .addCase(FETCH_STATUS_HISTORY_SUCCESS, (state, action) => {
      if (state.gate === 'disabled') {
        return;
      }
      const existing = state.historyByTdoId[action.payload.tdoId];
      state.historyByTdoId[action.payload.tdoId] = {
        records: mergeHistoryPage(
          existing?.records ?? [],
          action.payload.records
        ),
        count: action.payload.count,
        isLoading: false,
        error: null,
      };
    })
    .addCase(RESET_STATUS_HISTORY, (state, action) => {
      // Straight to loading, not to an empty list: the refetch that follows is
      // already in flight, and an empty-state flash would read as "this file
      // has no history" a beat before the tombstone arrives.
      state.historyByTdoId[action.payload.tdoId] = {
        records: [],
        count: 0,
        isLoading: true,
        error: null,
      };
    })
    .addCase(FETCH_STATUS_HISTORY_FAILURE, (state, action) => {
      const existing = state.historyByTdoId[action.payload.tdoId];
      state.historyByTdoId[action.payload.tdoId] = {
        records: existing?.records ?? [],
        count: existing?.count ?? 0,
        isLoading: false,
        error: action.payload.message,
      };
    });
});

export default reducer;

/** Reducer map for configureStore spreads (repo convention). */
export const statusHistoryReducer = {
  [namespace]: reducer,
};
